import {gsap} from "gsap";
import {TheCageGameAssets} from "../../TheCageGameAssets";
import {TheCage} from "../../TheCage";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {GSAPUtils} from "../../utils/GSAPUtils";
import {TheCageModel} from "../../server/TheCageModel";
import {TheCageGameMode} from "../../server/TheCageGameMode";
import {TheCageSound} from "../../sound/TheCageSound";
import {SpineGSAPWrapper} from "../../animation/SpineGSAPWrapper";

class LifeCounter extends PIXI.Container {
    protected background: PIXI.Sprite;
    protected meter: PIXI.spine.Spine;
    protected meterBlink: PIXI.spine.Spine;
    protected meterBlinkAnimation:string;
    protected meterSound: string;
    protected meterMask: PIXI.Graphics;
    protected delimContainer: PIXI.Container;
    protected meterY = -294;
    protected meterHeight = 588;

    protected maxLife:number;
    protected maxBarSize:number;
    protected beatUpLevel: number;
    protected tickDuration = '>0.25';

    constructor(maxLife:number, isOpponent: boolean) {
        super();
        this.maxLife = maxLife;
        this.maxBarSize = maxLife;

        this.background = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.FS_COUNTER_BACKGROUND)[0]);
        this.background.anchor.set(0.5, 0.5);

        this.meter = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_LIFE_COUNTER))
        this.meterBlink = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_LIFE_BLINK))
        if (isOpponent) {
            this.meter.state.setAnimation(0, "red", true);
            this.meterSound = TheCageSound.FS_ENEMY_HP_UP;
            this.meterBlinkAnimation = "redBlink";
        } else {
            this.meter.state.setAnimation(0, "blue", true);
            this.meterSound = TheCageSound.FS_HP_UP;
            this.meterBlinkAnimation = "blueBlink";

        }

        this.meterMask = new PIXI.Graphics();
        this.meterMask.beginFill(0xffffff, 1);
        this.meterMask.drawRect(-16, 0, 32, 588);
        this.meterMask.endFill();
        this.meterMask.position.set(0, -294)
        this.meterMask.height = 0;
        this.meter.mask = this.meterMask;

        this.delimContainer = new PIXI.Container;
        for (let i = 0; i < this.maxBarSize - 1; i++) {
            const newDelim = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.FS_COUNTER_DELIM)[0]);
            newDelim.anchor.set(0.5, 0.5);
            newDelim.position.set(0, this.meterY + (this.meterHeight / this.maxBarSize) * (1 + i));
            this.delimContainer.addChild(newDelim);
        }

        this.addChild(this.background);
        this.addChild(this.meter);
        this.addChild(this.meterMask);
        this.addChild(this.delimContainer)
        this.addChild(this.meterBlink);
        this.init();
    }

    protected updateBar() {
        this.meterMask.height = this.meterHeight - (this.meterHeight / this.maxBarSize) * this.beatUpLevel;
        this.meterMask.y = this.meterY + (this.meterHeight / this.maxBarSize) * this.beatUpLevel;
        this.meterBlink.y = this.meterMask.y + (this.meterHeight / this.maxBarSize) / 2;
    }

    public setMaxLife(newMaxLife: number) {
        this.maxLife = newMaxLife;
        this.maxBarSize = Math.max(newMaxLife, 8);
        this.delimContainer.removeChildren(0);
        for (let i = 0; i < this.maxBarSize - 1; i++) {
            const newDelim = new PIXI.Sprite(TheCage.assetLoader.getTextures(TheCageGameAssets.FS_COUNTER_DELIM)[0]);
            newDelim.anchor.set(0.5, 0.5);
            newDelim.position.set(0, this.meterY + (this.meterHeight / this.maxBarSize) * (1 + i));
            this.delimContainer.addChild(newDelim);
        }
    }

    public init(): void {
        this.beatUpLevel = this.maxLife;
        this.updateBar();
    }

    public restore(): void {
        this.init();
    }

    public setLife(hitsTaken:number): void {
        // adjust for if maxLife is less than 8 aka the smallest the meter can be
        this.beatUpLevel = hitsTaken + (this.maxBarSize - this.maxLife);
        this.updateBar();
    }

    public fillUp(): gsap.core.Timeline {
        const tl = gsap.timeline();
        const tickLambdas:Function[] = []
        // adjust for if maxLife is less than 8 aka the smallest the meter can be
        for (let i = 0; i < this.beatUpLevel - (this.maxBarSize - this.maxLife); i++) {
            tickLambdas.push(() => {
                this.beatUpLevel--;
                this.updateBar();
                TheCage.soundPlayer.playSound(this.meterSound);
                SpineGSAPWrapper.createTimeline(this.meterBlink, 0, this.meterBlinkAnimation);
            });
        }
        // adjust for if maxLife is less than 8 aka the smallest the meter can be
        tl.add(GSAPUtils.createTimelineFromArray(tickLambdas, `>${0.67 / (this.beatUpLevel - (this.maxBarSize - this.maxLife))}`));
        tl.add(() => {}, '>0.5');
        return tl;
    }

    public idle(): gsap.core.Timeline {
        return gsap.timeline();
    }

    public tickDown(punches: number): gsap.core.Timeline {
        return gsap.timeline().add(() => {
            this.beatUpLevel++;
            this.updateBar();
        });
    }
}

class LifeCounterLvl5 extends LifeCounter {
    private explodeSpine: PIXI.spine.Spine;
    private explodeSpinePortrait: PIXI.spine.Spine;
    private isLandscapeMode:boolean;

    constructor() {
        super(1, true);
        this.explodeSpine= new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_COUNTER_EXPLOSION));
        this.explodeSpinePortrait = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FS_COUNTER_EXPLOSION));
        this.addChild(this.explodeSpine);
        this.addChild(this.explodeSpinePortrait);

        this.background.visible = false;
        this.meter.visible = false;
        this.meterMask.visible = false;
        this.delimContainer.visible = false;
    }

    public init() {
        this.visible = true;
        this.meterMask.height = 0;
        if (this.explodeSpinePortrait != undefined) {
            this.explodeSpine.state.setEmptyAnimation(0, 0);
            this.explodeSpinePortrait.state.setEmptyAnimation(0, 0);
        }
    }

    public restore() {
        this.init()
        if (TheCage.model.data.mode != TheCageGameMode.NORMAL &&
            TheCage.model.data.mode != TheCageGameMode.NORMAL_AVALANCHE) {
            this.visible = false;
        }
    }

    public fillUp(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            this.visible = true;
            this.explodeSpine.state.setEmptyAnimation(0, 0);
            this.explodeSpinePortrait.state.setEmptyAnimation(0, 0);
        });
        tl.add(() => {TheCage.soundPlayer.playSound(TheCageSound.FS_ENEMY_HP_EXPLODE)});
        tl.add(SpineGSAPWrapper.createTimeline(this.explodeSpine, 0, "explosion_landscape"));
        tl.add(SpineGSAPWrapper.createTimeline(this.explodeSpinePortrait, 0, "explosion_portrait"), '<');
        tl.add(() => {this.visible = false;});
        tl.add(() => {}, '>0.5');
        return tl;
    }

    public setLandscapeMode(isLandscape: boolean):void {
        this.explodeSpinePortrait.visible = !isLandscape;
        this.explodeSpine.visible = isLandscape;
    }
}

export class FreespinsOpponentLifeCounterDelegate extends PIXI.Container {
    private counters: LifeCounter[];
    private currentCounter: LifeCounter;
    private previousCounter: LifeCounter;
    private isLandscape: boolean = true;

    constructor() {
        super();
        this.counters =  [
            new LifeCounter(TheCage.model.initData.BN_LEVEL_FS[0], true),
            new LifeCounter(TheCage.model.initData.BN_LEVEL_FS[1], true),
            new LifeCounter(TheCage.model.initData.BN_LEVEL_FS[2], true),
            new LifeCounter(TheCage.model.initData.BN_LEVEL_FS[3], true),
            new LifeCounterLvl5(),
        ];
        this.init(0);
    }

    public init(level: number):void {
        this.removeChildren(0);
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsOpponentLifeCounter";

        this.currentCounter.init();
        this.addChild(this.currentCounter)
    }

    public swapCounter(level: number):gsap.core.Timeline {
        this.previousCounter = this.currentCounter
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsOpponentLifeCounter";

        this.currentCounter.init();

        const tl = gsap.timeline();
        tl.add(() => {
            this.addChild(this.currentCounter)
            this.previousCounter.alpha = 1;
            this.currentCounter.alpha = 0;
        });
        tl.add(gsap.to(this.previousCounter, {alpha: 0, duration: 0.5}));
        tl.add(gsap.to(this.currentCounter, {alpha: 1, duration: 0.5}), '<');
        tl.add(() => {
            this.removeChild(this.previousCounter);
            this.previousCounter.alpha = 1;
        });
        return tl;
    }

    public restore(level: number): void {
        this.removeChildren(0);
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsPlayerLifeCounter";

        this.currentCounter.restore();
        this.currentCounter.setLife(TheCage.model.initData.nBNcollectedInFSAfter[level]);
        this.addChild(this.currentCounter)
    }

    private resizeCounter():void {
        if (this.isLandscape) {
            this.currentCounter.scale.set(1, 1);
            this.currentCounter.angle = 0;
            this.currentCounter.position.set(-120, 360);
        } else {
            this.currentCounter.scale.set(0.8, 0.5);
            this.currentCounter.angle = 90;
            this.currentCounter.position.set(180, 700);
        }
    }

    public setLandscapeMode(isLandscape: boolean): void {
        this.isLandscape = isLandscape;
        (<LifeCounterLvl5>this.counters[4]).setLandscapeMode(isLandscape);
        this.resizeCounter();
    }

    // details about the reveal, most likely skip "darkened idle" and just reveal from darkened then regular idle
    public fillUp(): gsap.core.Timeline {
        return this.currentCounter.fillUp();
    }

    public idle(): gsap.core.Timeline {
        return this.currentCounter.idle();
    }

    public tickDown(punches: number): gsap.core.Timeline {
        return this.currentCounter.tickDown(punches);
    }
}

export class FreespinsPlayerLifeCounterDelegate extends PIXI.Container {
    private counters: LifeCounter[];
    private currentCounter: LifeCounter;
    private previousCounter: LifeCounter;
    private isLandscape: boolean = true;

    constructor() {
        super();
        this.counters =  [
            new LifeCounter(6, false),
            new LifeCounter(9, false),
            new LifeCounter(12, false),
            new LifeCounter(15, false),
            new LifeCounter(20, false),
        ];
        this.init(0);
    }

    public init(level: number):void {
        this.removeChildren(0);
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsPlayerLifeCounter";

        this.currentCounter.setMaxLife(TheCage.model.data.freespinsLeft);
        this.currentCounter.init();
        this.addChild(this.currentCounter)
    }

    public swapCounter(level: number):gsap.core.Timeline {
        this.previousCounter = this.currentCounter;
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsPlayerLifeCounter";

        const fsLeft = TheCage.model.data.freespinsLeft;
        const additionalFs = TheCage.model.data.additionalFSadded;
        this.currentCounter.setMaxLife(fsLeft);
        this.currentCounter.init();
        this.currentCounter.setLife(additionalFs);

        const tl = gsap.timeline();
        tl.add(() => {
            this.addChild(this.currentCounter)
            this.previousCounter.alpha = 1;
            this.currentCounter.alpha = 0;
        });
        tl.add(gsap.to(this.previousCounter, {alpha: 0, duration: 0.5}));
        tl.add(gsap.to(this.currentCounter, {alpha: 1, duration: 0.5}), '<');
        tl.add(() => {
            this.removeChild(this.previousCounter);
            this.previousCounter.alpha = 1;
        });
        return tl;
    }

    public restore(level: number): void {
        this.removeChildren(0);
        this.currentCounter = this.counters[level];

        this.resizeCounter();
        this.currentCounter.name = "freespinsPlayerLifeCounter";

        //const meterSize = level == TheCage.model.initData.fsLevelEnterWith ? 8 : TheCage.model.initData.freespinMeterSizeBefore;
        const meterSize = (TheCage.model.initData.mode == TheCageGameMode.NORMAL ||
                           TheCage.model.initData.mode == TheCageGameMode.NORMAL_AVALANCHE) ||
                          (TheCage.model.initData.nBNcollectedInFSAfter[level] != TheCage.model.initData.nBNcollectedInFSBefore[level])
                          ? TheCage.model.initData.freespinMeterSizeBefore
                          : TheCage.model.initData.freespinMeterSizeAfter;
        this.currentCounter.setMaxLife(meterSize);
        this.currentCounter.init();
        this.currentCounter.setLife(meterSize -
                                    (TheCage.model.initData.freespinsLeft - TheCage.model.initData.additionalFSadded));
        this.addChild(this.currentCounter)
    }

    private resizeCounter():void {
        if (this.isLandscape) {
            this.currentCounter.scale.set(1, 1);
            this.currentCounter.angle = 0;
            this.currentCounter.position.set(840, 360);
        } else {
            this.currentCounter.scale.set(0.8, 0.5);
            this.currentCounter.angle = -90;
            this.currentCounter.position.set(540, 700);
        }
    }

    public setLandscapeMode(isLandscape: boolean): void {
        this.isLandscape = isLandscape;
        this.resizeCounter();
    }

    // details about the reveal, most likely skip "darkened idle" and just reveal from darkened then regular idle
    public fillUp(): gsap.core.Timeline {
        return this.currentCounter.fillUp();
    }

    public idle(): gsap.core.Timeline {
        return this.currentCounter.idle();
    }

    public tickDown(punches: number): gsap.core.Timeline {
        return this.currentCounter.tickDown(punches);
    }
}